import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';

const TimerContext = createContext(null);

export const TimerProvider = ({ children }) => {
  const { api } = useAuth();
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [runningEntry, setRunningEntry] = useState(null);
  const [idlePausedAt, setIdlePausedAt] = useState(null);
  const intervalRef = useRef(null);
  const pausedTimeRef = useRef(0);

  // Idle detection state
  const [showIdleDialog, setShowIdleDialog] = useState(false);
  const [idleMinutes, setIdleMinutes] = useState(0);
  const lastUserActivityRef = useRef(Date.now());
  const lastTickRef = useRef(Date.now());
  const idleAlreadyTriggeredRef = useRef(false);

  // Workspace settings for idle detection
  const [idleSettings, setIdleSettings] = useState({
    idle_detection_enabled: true,
    idle_threshold: 10, // minutes
    idle_action: 'auto_pause'
  });

  // Fetch workspace settings
  useEffect(() => {
    const fetchSettings = async () => {
      if (!api) return;
      try {
        const res = await api.get('/workspace-settings');
        if (res.data) {
          setIdleSettings({
            idle_detection_enabled: res.data.idle_detection_enabled !== false,
            idle_threshold: res.data.idle_threshold || 10,
            idle_action: res.data.idle_action || 'auto_pause'
          });
        }
      } catch (e) {
        // Use defaults
      }
    };
    fetchSettings();
  }, [api]);

  // Global user activity tracking — works across ALL pages
  useEffect(() => {
    const resetActivity = () => {
      lastUserActivityRef.current = Date.now();
    };

    window.addEventListener('mousemove', resetActivity);
    window.addEventListener('keydown', resetActivity);
    window.addEventListener('click', resetActivity);
    window.addEventListener('scroll', resetActivity);
    window.addEventListener('touchstart', resetActivity);

    return () => {
      window.removeEventListener('mousemove', resetActivity);
      window.removeEventListener('keydown', resetActivity);
      window.removeEventListener('click', resetActivity);
      window.removeEventListener('scroll', resetActivity);
      window.removeEventListener('touchstart', resetActivity);
    };
  }, []);

  // When tab becomes visible again, check how long user was away
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isRunning && !isPaused) {
        const now = Date.now();
        const thresholdMs = (idleSettings.idle_detection_enabled ? idleSettings.idle_threshold : 999999) * 60 * 1000;
        const timeSinceActivity = now - lastUserActivityRef.current;

        if (timeSinceActivity > thresholdMs && !idleAlreadyTriggeredRef.current) {
          const minutesIdle = Math.floor(timeSinceActivity / 60000);
          idleAlreadyTriggeredRef.current = true;
          setIdleMinutes(minutesIdle);
          setIdlePausedAt(new Date().toISOString());
          setIsPaused(true);
          setShowIdleDialog(true);
        }

        // Reset tick ref so timer tick doesn't double-detect
        lastTickRef.current = now;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isRunning, isPaused, idleSettings]);

  // Check for running timer on mount
  useEffect(() => {
    const checkRunningTimer = async () => {
      if (!api) return;
      try {
        const response = await api.get('/time-entries/running');
        if (response.data && response.data.id) {
          setRunningEntry(response.data);
          setIsRunning(true);
          setIsPaused(false);
          const startTime = new Date(response.data.start_time);
          const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
          setElapsedTime(elapsed);
        }
      } catch (error) {
        // No running timer
      }
    };
    checkRunningTimer();
  }, [api]);

  // Timer tick with idle detection
  useEffect(() => {
    if (isRunning && !isPaused) {
      lastTickRef.current = Date.now();
      idleAlreadyTriggeredRef.current = false;

      intervalRef.current = setInterval(() => {
        const now = Date.now();

        if (!idleSettings.idle_detection_enabled) {
          setElapsedTime(prev => prev + 1);
          lastTickRef.current = now;
          return;
        }

        const thresholdMs = idleSettings.idle_threshold * 60 * 1000;

        // Check 1: Tick gap detection (handles sleep/wake and heavy browser throttling)
        const tickGap = now - lastTickRef.current;
        if (tickGap > thresholdMs) {
          const minutesMissed = Math.floor(tickGap / 60000);
          idleAlreadyTriggeredRef.current = true;
          setIdleMinutes(minutesMissed);
          setIdlePausedAt(new Date().toISOString());
          setIsPaused(true);
          setShowIdleDialog(true);
          lastTickRef.current = now;
          return;
        }

        // Check 2: No user activity for longer than threshold
        const timeSinceActivity = now - lastUserActivityRef.current;
        if (timeSinceActivity > thresholdMs && !idleAlreadyTriggeredRef.current) {
          const minutesIdle = Math.floor(timeSinceActivity / 60000);
          idleAlreadyTriggeredRef.current = true;
          setIdleMinutes(minutesIdle);
          setIdlePausedAt(new Date().toISOString());
          setIsPaused(true);
          setShowIdleDialog(true);
          lastTickRef.current = now;
          return;
        }

        // Normal tick
        setElapsedTime(prev => prev + 1);
        lastTickRef.current = now;
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, isPaused, idleSettings]);

  const formatTime = useCallback((seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const startTimer = useCallback(async (entryData = {}) => {
    if (!api) return;
    try {
      const response = await api.post('/time-entries/start', {
        description: entryData.description || '',
        project_id: entryData.project_id || null,
        task_id: entryData.task_id || null,
        billable: entryData.billable !== undefined ? entryData.billable : true,
        tags: entryData.tags || []
      });
      setRunningEntry(response.data);
      setIsRunning(true);
      setIsPaused(false);
      setElapsedTime(0);
      pausedTimeRef.current = 0;
      lastUserActivityRef.current = Date.now();
      idleAlreadyTriggeredRef.current = false;
      return response.data;
    } catch (error) {
      console.error('Error starting timer:', error);
      throw error;
    }
  }, [api]);

  const pauseTimer = useCallback((isIdlePause = false) => {
    if (isRunning && !isPaused) {
      setIsPaused(true);
      pausedTimeRef.current = elapsedTime;
      if (isIdlePause) {
        setIdlePausedAt(new Date());
      }
    }
  }, [isRunning, isPaused, elapsedTime]);

  const resumeTimer = useCallback(() => {
    if (isRunning && isPaused) {
      setIsPaused(false);
      setIdlePausedAt(null);
      setShowIdleDialog(false);
      setIdleMinutes(0);
      idleAlreadyTriggeredRef.current = false;
      lastTickRef.current = Date.now();
      lastUserActivityRef.current = Date.now();
    }
  }, [isRunning, isPaused]);

  // Handle idle dialog - keep idle time (count it as work)
  const keepIdleTime = useCallback(() => {
    setShowIdleDialog(false);
    setIsPaused(false);
    setIdlePausedAt(null);
    setIdleMinutes(0);
    idleAlreadyTriggeredRef.current = false;
    lastTickRef.current = Date.now();
    lastUserActivityRef.current = Date.now();
  }, []);

  // Handle idle dialog - discard idle time (subtract from elapsed)
  const discardIdleTime = useCallback((minutes) => {
    const secondsToDiscard = (minutes || idleMinutes) * 60;
    setElapsedTime(prev => Math.max(0, prev - secondsToDiscard));
    setShowIdleDialog(false);
    setIsPaused(false);
    setIdlePausedAt(null);
    setIdleMinutes(0);
    idleAlreadyTriggeredRef.current = false;
    lastTickRef.current = Date.now();
    lastUserActivityRef.current = Date.now();
  }, [idleMinutes]);

  // Stop timer with optional idle deduction
  const stopTimer = useCallback(async (idleSecondsToDeduct = 0) => {
    if (!api || !runningEntry) return;
    try {
      const response = await api.post('/time-entries/stop', {
        idle_seconds: idleSecondsToDeduct
      });
      const result = response.data;
      setIsRunning(false);
      setIsPaused(false);
      setElapsedTime(0);
      setRunningEntry(null);
      setShowIdleDialog(false);
      setIdleMinutes(0);
      setIdlePausedAt(null);
      pausedTimeRef.current = 0;
      idleAlreadyTriggeredRef.current = false;
      return result;
    } catch (error) {
      console.error('Error stopping timer:', error);
      throw error;
    }
  }, [api, runningEntry]);

  const discardTimer = useCallback(async () => {
    if (!api || !runningEntry) return;
    try {
      await api.delete(`/time-entries/${runningEntry.id}`);
      setIsRunning(false);
      setIsPaused(false);
      setElapsedTime(0);
      setRunningEntry(null);
      setShowIdleDialog(false);
      setIdleMinutes(0);
      setIdlePausedAt(null);
      pausedTimeRef.current = 0;
      idleAlreadyTriggeredRef.current = false;
    } catch (error) {
      console.error('Error discarding timer:', error);
      throw error;
    }
  }, [api, runningEntry]);

  // Reset timer state (used on logout)
  const resetTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setIsRunning(false);
    setIsPaused(false);
    setElapsedTime(0);
    setRunningEntry(null);
    setIdlePausedAt(null);
    setShowIdleDialog(false);
    setIdleMinutes(0);
    pausedTimeRef.current = 0;
    idleAlreadyTriggeredRef.current = false;
  }, []);

  const updateRunningEntry = useCallback(async (updates) => {
    if (!api || !runningEntry) return;
    try {
      const response = await api.put(`/time-entries/${runningEntry.id}`, updates);
      setRunningEntry(response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating entry:', error);
      throw error;
    }
  }, [api, runningEntry]);

  const continueEntry = useCallback(async (entry) => {
    if (!api) return;
    try {
      if (isRunning && runningEntry) {
        await api.post('/time-entries/stop');
      }
      const response = await api.post(`/time-entries/${entry.id}/resume`);
      const resumed = response.data;
      setRunningEntry(resumed);
      setIsRunning(true);
      setIsPaused(false);
      const startTime = new Date(resumed.start_time);
      const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
      setElapsedTime(elapsed);
      pausedTimeRef.current = 0;
      lastUserActivityRef.current = Date.now();
      idleAlreadyTriggeredRef.current = false;
      return resumed;
    } catch (error) {
      console.error('Error resuming entry:', error);
      throw error;
    }
  }, [api, isRunning, runningEntry]);

  const refreshTimer = useCallback(async () => {
    if (!api) return;
    try {
      const response = await api.get('/time-entries/running');
      if (response.data && response.data.id) {
        setRunningEntry(response.data);
        setIsRunning(true);
        setIsPaused(false);
        const startTime = new Date(response.data.start_time);
        const elapsed = Math.floor((Date.now() - startTime.getTime()) / 1000);
        setElapsedTime(elapsed);
      } else {
        setRunningEntry(null);
        setIsRunning(false);
        setIsPaused(false);
        setElapsedTime(0);
      }
    } catch (error) {
      setRunningEntry(null);
      setIsRunning(false);
      setElapsedTime(0);
    }
  }, [api]);

  return (
    <TimerContext.Provider value={{
      isRunning,
      isPaused,
      elapsedTime,
      runningEntry,
      idlePausedAt,
      showIdleDialog,
      idleMinutes,
      formatTime,
      startTimer,
      pauseTimer,
      resumeTimer,
      stopTimer,
      discardTimer,
      resetTimer,
      discardIdleTime,
      keepIdleTime,
      updateRunningEntry,
      continueEntry,
      refreshTimer
    }}>
      {children}
    </TimerContext.Provider>
  );
};

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimer must be used within a TimerProvider');
  }
  return context;
};
